import { useState, useRef, useEffect, useCallback } from "react";
import { X, ChevronRight, FolderKanban, GripVertical } from "lucide-react";

export default function ProjectPanel({ projet, onClose, onSelectDevice }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);

  // Reset la position quand on change de projet
  useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [projet?._id]);

  const handlePointerDown = useCallback((e) => {
    if (e.target.closest('button')) return; // ne pas démarrer le drag depuis la croix
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const parent = panelRef.current?.offsetParent;
      if (!parent || !panelRef.current) return;
      const parentRect = parent.getBoundingClientRect();
      const panelRect = panelRef.current.getBoundingClientRect();
      let x = e.clientX - parentRect.left - dragOffset.current.x;
      let y = e.clientY - parentRect.top - dragOffset.current.y;
      // Borner dans le conteneur de la carte
      x = Math.max(0, Math.min(x, parentRect.width - panelRect.width));
      y = Math.max(0, Math.min(y, parentRect.height - panelRect.height));
      setPosition({ x, y });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [dragging]);

  if (!projet) return null;

  const total = projet.total || projet.appareils?.length || 0;
  const actifs = projet.actifs ?? projet.appareils?.filter(a => a.etat === "En ligne").length ?? 0;
  const pct = total > 0 ? Math.round((actifs / total) * 100) : 0;
  const color = pct === 100 ? "text-green-600" : pct >= 90 ? "text-yellow-500" : "text-red-500";
  const dotColor = pct === 100 ? "bg-green-500" : pct >= 90 ? "bg-yellow-400" : "bg-red-500";

  return (
    <div
      ref={panelRef}
      className={`absolute w-96 bg-white z-[1000] shadow-2xl rounded-r-xl overflow-hidden flex flex-col ${dragging ? '' : 'transition-shadow'}`}
      style={{
        top: position.y,
        left: position.x,
        maxHeight: 'calc(100% - 8px)',
        cursor: dragging ? 'grabbing' : 'default',
        userSelect: dragging ? 'none' : 'auto',
      }}
    >
      <div
        className="p-4 border-b border-gray-100 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={handlePointerDown}
        title="Glisser pour déplacer"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <GripVertical size={14} className="text-slate-300 flex-shrink-0" />
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FolderKanban size={18} className="text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800 text-sm leading-tight truncate">{projet.nom}</h3>
              <p className="text-xs text-slate-400 truncate">{projet.adresse || ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <p className="text-xs text-slate-400">Informations détaillées du projet avec statistiques et liste complète des appareils</p>
      </div>

      <div className="overflow-y-auto flex-1">
        <div className="p-4 grid grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 mb-1">Client</p>
            <p className="text-xs font-semibold text-slate-700">{projet.clientNom || "—"}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 mb-1">Revendeur</p>
            <p className="text-xs font-semibold text-slate-700">{projet.revendeurNom || "—"}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 mb-1">Statut</p>
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${dotColor}`} />
              <p className={`text-xs font-bold ${color}`}>{pct}% actifs</p>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Statistiques des appareils</p>
            <div className="flex gap-6">
              <div>
                <p className="text-xs text-slate-400">Total</p>
                <p className="text-2xl font-bold text-slate-800">{total}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Actifs</p>
                <p className="text-2xl font-bold text-green-600">{actifs}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-700">Appareils du projet</p>
            <span className="text-xs bg-gray-100 text-slate-600 px-2 py-1 rounded-full">{total} appareils</span>
          </div>
          <div className="space-y-2">
            {(projet.appareils || []).map((a) => {
              const isActif = a.etat === "En ligne";
              return (
                <button
                  key={a._id || a.sn}
                  onClick={() => onSelectDevice(a)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all hover:shadow-sm ${
                    isActif ? "bg-green-50 border-green-100 hover:border-green-300" : "bg-red-50 border-red-100 hover:border-red-300"
                  }`}
                >
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800">{a.sn}</p>
                    <p className="text-xs text-slate-400">{a.type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${isActif ? "bg-green-500" : "bg-red-500"}`} />
                    <ChevronRight size={14} className="text-slate-300" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
